/**
 * StudiesList — the Study Hub landing page. CRM studies as cards (status,
 * lead count, prescreen state, stipend hint); studies seen only in lead data
 * appear as "from leads" stubs that an admin can adopt (pre-filled create).
 *
 * Link-or-create (source-of-truth plan §5.1): while registry reads are on, the
 * "New study" modal is StudyPicker-first — pick the eReg registry record
 * (network-wide search via the CRM proxy) or follow "Create new study in
 * eReg", which returns here with ?created_protocol_id=<id> and auto-selects
 * it. A new CRM study cannot be created unlinked.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Archive, ArchiveRestore, CheckCircle2, FlaskConical, MoreVertical, Plus, Trash2, Users } from 'lucide-react';
import api from '../../../services/api';
import { StudyOption, studyInternalLabel } from '../../types';
import { studyStatusMeta, useIsCRMAdmin, usePersistedState } from '../../lib';
import { FilterBar, PageHeader, SearchInput, SegmentedTabs } from '../../components';
import type { SegTab } from '../../components';
import StudyPicker, { RegistryStudy } from '../../../ui/StudyPicker';

interface CreateForm {
  title: string;
  internal_title: string;
  slug: string;
  sponsor_name: string;
  therapeutic_area: string;
  status: string;
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const slugify = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

function CreateStudyModal({
  initial,
  registryEnabled,
  initialRegistryStudy,
  onClose,
  onCreated,
}: {
  initial: Partial<CreateForm>;
  /** StudyPicker-first link-or-create (plan §5.1); false = legacy modal. */
  registryEnabled: boolean;
  /** Pre-selected registry record (the ?created_protocol_id return flow). */
  initialRegistryStudy?: RegistryStudy | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [form, setForm] = useState<CreateForm>({
    title: initial.title || '',
    internal_title: initial.internal_title || '',
    slug: initial.slug || '',
    sponsor_name: initial.sponsor_name || '',
    therapeutic_area: initial.therapeutic_area || '',
    // New studies start as Draft so the setup checklist can guide configuration
    // before the final "Set to Recruiting" step takes the study live (and makes
    // it matchable in cross-study pivots).
    status: initial.status || 'draft',
  });
  const [slugTouched, setSlugTouched] = useState(Boolean(initial.slug));
  const [registryStudy, setRegistryStudy] = useState<RegistryStudy | null>(initialRegistryStudy || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Picking a registry record prefills the CRM-only fields (suggestions —
  // everything stays editable; blanks only, never overwriting typed values).
  const pickRegistryStudy = (s: RegistryStudy) => {
    setRegistryStudy(s);
    setForm((f) => ({
      ...f,
      title: f.title || s.study_nickname || '',
      internal_title: f.internal_title || s.study_nickname || s.protocol_number || '',
      slug: slugTouched ? f.slug : slugify(f.title || s.study_nickname || ''),
      sponsor_name: f.sponsor_name || s.sponsor_name || '',
      therapeutic_area: f.therapeutic_area || s.therapeutic_area || '',
    }));
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.post('/api/crm/studies', {
        title: form.title,
        internal_title: form.internal_title.trim(),
        slug: form.slug || undefined,
        sponsor_name: form.sponsor_name || null,
        therapeutic_area: form.therapeutic_area || null,
        status: form.status,
        ...(registryEnabled ? {
          protocol_id: registryStudy?.id || null,
          // The registry record's protocol number rides onto the CRM study's
          // own field so recruiter surfaces show it without a registry read.
          ...(registryStudy?.protocol_number ? { protocol_number: registryStudy.protocol_number } : {}),
        } : {}),
      });
      onCreated(res.data.study.id);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to create study');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">New study</h3>
        {registryEnabled && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              eReg study (required — the source of truth for this study’s documents)
            </label>
            {registryStudy ? (
              <div className="flex items-start gap-2.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 dark:border-primary-800 dark:bg-primary-900/20">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {registryStudy.protocol_number}
                  </p>
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {[registryStudy.study_nickname, registryStudy.sponsor_name, registryStudy.site?.name].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setRegistryStudy(null)}
                  className="inline-flex min-h-[44px] items-center rounded-lg px-2 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-900/40 sm:min-h-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <StudyPicker
                searchPath="/api/crm/registry/studies"
                onSelect={pickRegistryStudy}
                createReturnTo="/crm/settings/studies"
                createReturnLabel="CRM Studies"
                placeholder="Search eReg studies by protocol number, name, or sponsor…"
              />
            )}
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Documents (protocol, ICF) are managed in eReg; the CRM reads the current versions from there.
            </p>
          </div>
        )}
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Public title</label>
            <input
              type="text" value={form.title} autoFocus className={inputCls}
              onChange={(e) => setForm((f) => ({
                ...f,
                title: e.target.value,
                slug: slugTouched ? f.slug : slugify(e.target.value),
              }))}
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Patient-facing — shown on the public website, in SMS/email, and in the URL.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Internal title</label>
            <input
              type="text" value={form.internal_title} maxLength={300} className={inputCls}
              onChange={(e) => setForm((f) => ({ ...f, internal_title: e.target.value }))}
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Required. Only recruiters/admins see this across the CRM; never shown to patients.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Slug (joins leads; locked once a prescreen is published)
            </label>
            <input
              type="text" value={form.slug} className={`${inputCls} font-mono`}
              onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: slugify(e.target.value) })); }}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Sponsor</label>
              <input type="text" value={form.sponsor_name} className={inputCls}
                onChange={(e) => setForm((f) => ({ ...f, sponsor_name: e.target.value }))} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Therapeutic area</label>
              <input type="text" placeholder="e.g. Endocrinology" value={form.therapeutic_area} className={inputCls}
                onChange={(e) => setForm((f) => ({ ...f, therapeutic_area: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
            <select value={form.status} className={inputCls} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              <option value="draft">Draft</option>
              <option value="recruiting">Recruiting</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button type="button" onClick={submit}
            disabled={saving || !form.title.trim() || !form.internal_title.trim() || (registryEnabled && !registryStudy)}
            className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50">
            {saving ? 'Creating…' : 'Create study'}
          </button>
        </div>
      </div>
    </div>
  );
}

type StudyFilter = 'all' | 'recruiting' | 'draft' | 'paused' | 'closed' | 'archived';
const FILTERS: { value: StudyFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'recruiting', label: 'Recruiting' },
  { value: 'draft', label: 'Draft' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

export default function StudiesList() {
  const navigate = useNavigate();
  const isAdmin = useIsCRMAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<Partial<CreateForm> | null>(null);
  const [registryEnabled, setRegistryEnabled] = useState(true);
  // The registry record pre-selected by the create-in-eReg return flow.
  const [pendingRegistryStudy, setPendingRegistryStudy] = useState<RegistryStudy | null>(null);
  const [filter, setFilter] = usePersistedState<StudyFilter>('studies.filter', 'recruiting');
  const [search, setSearch] = useState(''); // client-side title/slug/sponsor filter (not persisted)
  const [menuFor, setMenuFor] = useState<string | null>(null); // study id with its action menu open
  const [acting, setActing] = useState<string | null>(null);   // study id being archived/deleted

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/crm/studies');
      setStudies(res.data?.studies || []);
      setRegistryEnabled(res.data?.registry_reads_enabled !== false);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load studies');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Return leg of "Create new study in eReg" (§3.6): eReg navigates back here
  // with ?created_protocol_id=<id>. Resolve it, reopen the modal with the new
  // registry record pre-selected, and clear the param (refresh-safe).
  useEffect(() => {
    const createdId = searchParams.get('created_protocol_id');
    if (!createdId || !isAdmin) return;
    setSearchParams({}, { replace: true });
    api.get(`/api/crm/registry/studies/${createdId}`)
      .then((res) => {
        const s = res.data;
        setPendingRegistryStudy(s);
        // Same prefill the interactive pick applies — the two selection paths
        // must land the admin in an identical, submit-ready modal.
        setCreating({
          title: s?.study_nickname || '',
          internal_title: s?.study_nickname || s?.protocol_number || '',
          slug: slugify(s?.study_nickname || ''),
          sponsor_name: s?.sponsor_name || '',
          therapeutic_area: s?.therapeutic_area || '',
        });
      })
      .catch(() => {
        // Non-fatal: the study exists in eReg — open the modal so the admin
        // can search for it (never blank the loaded list with a page error).
        setCreating({});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  const crmStudies = studies.filter((s) => s.source === 'crm');
  const leadStubs = studies.filter((s) => s.source === 'leads');

  // Status predicate + per-tab counts. Counts come from the full study set (not
  // the search box) so the tabs stay stable while the recruiter types.
  const matches = (s: StudyOption, f: StudyFilter) =>
    f === 'archived' ? !!s.archived_at
      : f === 'all' ? !s.archived_at
        : !s.archived_at && s.status === f;
  const counts = Object.fromEntries(FILTERS.map((f) => [f.value, crmStudies.filter((s) => matches(s, f.value)).length])) as Record<StudyFilter, number>;
  // Client-side search narrows the status-filtered set by title / slug / sponsor.
  const q = search.trim().toLowerCase();
  const visibleStudies = crmStudies
    .filter((s) => matches(s, filter))
    .filter((s) =>
      !q ||
      [s.title, s.slug, s.sponsor_name, s.therapeutic_area]
        .some((v) => (v || '').toLowerCase().includes(q)),
    );
  const statusTabs: SegTab[] = FILTERS.map((f) => ({ value: f.value, label: f.label, count: counts[f.value] }));

  const archive = async (s: StudyOption, archived: boolean) => {
    if (!s.id) return;
    setMenuFor(null);
    setActing(s.id);
    setError(null);
    try {
      await api.post(`/api/crm/studies/${s.id}/archive`, { archived });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to archive study');
    } finally {
      setActing(null);
    }
  };

  const del = async (s: StudyOption) => {
    if (!s.id) return;
    setMenuFor(null);
    if (!window.confirm(`Permanently delete "${s.title}"? This cannot be undone.\n\n(Studies with screening history can't be deleted — archive them instead.)`)) return;
    setActing(s.id);
    setError(null);
    try {
      await api.delete(`/api/crm/studies/${s.id}`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to delete study');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={FlaskConical}
        title="Studies"
        subtitle="Per-study recruiter toolkits: summary, script, stipend, and prescreen."
        actions={
          isAdmin && (
            <button
              type="button"
              onClick={() => setCreating({})}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 sm:min-h-0"
            >
              <Plus className="h-4 w-4" /> New study
            </button>
          )
        }
      />

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : (
        <>
          {crmStudies.length === 0 && leadStubs.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
              <FlaskConical className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No studies yet.{isAdmin && ' Create your first study to start building its recruiter toolkit.'}
              </p>
            </div>
          )}

          {crmStudies.length > 0 && (
            <>
              <SegmentedTabs
                label="Study status"
                tabs={statusTabs}
                value={filter}
                onChange={(v) => setFilter(v as StudyFilter)}
              />
              <FilterBar>
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search studies by name, slug, or sponsor…"
                  label="Search studies"
                  className="flex-1 basis-56"
                />
              </FilterBar>
            </>
          )}

          {crmStudies.length > 0 && visibleStudies.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
              {q
                ? 'No studies match your search.'
                : `No ${filter === 'all' ? '' : `${filter} `}studies to show.`}
            </div>
          )}

          {visibleStudies.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {visibleStudies.map((s) => {
                const meta = studyStatusMeta(s.status);
                const stipend = s.stipend_summary?.amount_text;
                const archived = !!s.archived_at;
                return (
                  <div key={s.slug} className="relative">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/crm/settings/studies/${s.id}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/crm/settings/studies/${s.id}`); } }}
                      className={`block cursor-pointer rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors duration-150 hover:border-primary-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-700 ${archived ? 'opacity-70' : ''}`}
                    >
                      <div className={`flex items-start justify-between gap-2 ${isAdmin ? 'pr-6' : ''}`}>
                        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{studyInternalLabel(s)}</h2>
                        <div className="flex flex-shrink-0 items-center gap-1.5">
                          {archived && <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">Archived</span>}
                          {s.show_on_website && !archived && (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">Live</span>
                          )}
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.badge}`}>{meta.label}</span>
                        </div>
                      </div>
                      {s.internal_title && s.title && s.internal_title !== s.title && (
                        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400" title="Public title">{s.title}</p>
                      )}
                      <p className="mt-0.5 truncate font-mono text-xs text-gray-400 dark:text-gray-500">{s.slug}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {s.count} lead{s.count === 1 ? '' : 's'}</span>
                        {s.prescreen_version ? (
                          <span className="text-purple-600 dark:text-purple-400">Prescreen v{s.prescreen_version}</span>
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400">No prescreen yet</span>
                        )}
                        {stipend && <span className="text-green-700 dark:text-green-400">{stipend}</span>}
                      </div>
                    </div>

                    {isAdmin && (
                      <div className="absolute right-2 top-2">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === s.id ? null : (s.id || null)); }}
                          disabled={acting === s.id}
                          aria-label="Study actions"
                          className="rounded-lg p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-700"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {menuFor === s.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setMenuFor(null)} />
                            <div className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                              <button
                                type="button"
                                onClick={() => archive(s, !archived)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                {archived ? 'Unarchive' : 'Archive'}
                              </button>
                              <button
                                type="button"
                                onClick={() => del(s)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 transition-colors duration-150 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="h-4 w-4" /> Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {leadStubs.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Seen in lead data</h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Studies your leads came in for that don't have a CRM workspace yet.
              </p>
              <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
                {leadStubs.map((s) => (
                  <li key={s.slug} className="flex items-center gap-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{s.title || s.slug}</p>
                      <p className="font-mono text-xs text-gray-400 dark:text-gray-500">{s.slug} · {s.count} lead{s.count === 1 ? '' : 's'}</p>
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => setCreating({ title: s.title || s.slug, slug: s.slug })}
                        className="rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                      >
                        Set up workspace
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {creating && (
        <CreateStudyModal
          initial={creating}
          registryEnabled={registryEnabled}
          initialRegistryStudy={pendingRegistryStudy}
          onClose={() => { setCreating(null); setPendingRegistryStudy(null); }}
          onCreated={(id) => navigate(`/crm/settings/studies/${id}?setup=1`)}
        />
      )}
    </div>
  );
}
